import { IBrowserLocation, IGeoDataByIp, IGeoDataByLatLng, IUserLocation } from '../models/Location';
import * as Sentry from '@sentry/nextjs';
import { axiosApi } from './axios';

// 1) Try to get browser location first
// 2) Regardless whether browser location data exists, also get data from IpStack
// 3) Run geocode api against this data

export async function getUserLocation(
  bypassBrowserLocation = false,
  signal?: AbortSignal | null,
  browserIp?: string | undefined
): Promise<IUserLocation> {
  let browserLat, browserLong;

  if (!bypassBrowserLocation) {
    //console.log("try to get browser location");
    const browserLocation = await getBrowserLocation();
    browserLat = browserLocation.lat;
    browserLong = browserLocation.long;
    //console.log("browserLocation: "+browserLat+" "+browserLong);
  }

  if (signal && signal.aborted) {
    return {};
  }

  const ipLocation = await getGeoLocationByIp(browserIp);

  let geoLocation;
  if (browserLat && browserLong) {
    geoLocation = await getGeoLocationByLatLong(browserLat, browserLong);
  }

  return {
    ip: ipLocation?.ip,
    city: ipLocation?.city,
    zip: ipLocation?.zip,
    lat: browserLat || ipLocation?.lat,
    long: browserLong || ipLocation?.long,
    state: geoLocation?.state || ipLocation?.state,
  };
}

async function getBrowserLocation(): Promise<Partial<IBrowserLocation>> {
  try {
    if (typeof navigator !== 'undefined' && 'geolocation' in navigator) {
      const position: GeolocationPosition = await new Promise((resolve, reject) => {
        const onSuccess = (position: GeolocationPosition) => resolve(position);
        const onError = (error: GeolocationPositionError) => reject(error);

        navigator.geolocation.getCurrentPosition(onSuccess, onError);
      });

      return { lat: position?.coords?.latitude, long: position?.coords?.longitude };
    }
    //updated error handling to only capture legit exceptions
  } catch (err: any) {
    // Check if the error is a GeolocationPositionError by looking for the 'code' property
    if (err && typeof err.code !== 'undefined') {
      console.warn(`Geolocation Error Occurred: Code=${err.code}, Message=${err?.message}`);
      if (process.env.NEXT_PUBLIC_ENV === 'production' && err?.message?.toLowerCase() !== 'user denied geolocation') {
        //Sentry.captureMessage(`Geolocation Error Occurred: Code=${err.code}, Message=${err?.message}`, "warning");
      } else if (err instanceof Error) {
        //console.error('Geolocation Error: ', err?.message);
      }
    }
  }

  return {};
}

async function getGeoLocationByIp(browserIp: string | undefined): Promise<Partial<IGeoDataByIp>> {
  // don't call the api if this is a local test
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    console.log('this is local debug - returning dummy values for location');
    return {
      ip: '127.0.0.1',
      city: 'Local City',
      zip: '12345',
      state: 'LC',
      lat: 0,
      long: 0,
    };
  }

  try {
    const { data: location } = await axiosApi.get(
      `https://api.ipstack.com/check?access_key=${process.env.NEXT_PUBLIC_IPSTACK_ACCESS_KEY}&output=json`,
      {
        timeout: 5000,
      }
    );

    if (!location?.ip) {
      //TODO: fallback to different method to get geolocation info city, state, zip, etc before throwing error
      Sentry.captureException('unable to get location info from ipstack');
    }

    return {
      ip: location?.ip || browserIp,
      city: location.city,
      zip: location.zip,
      state: location.region_code,
      lat: location.latitude,
      long: location.longitude,
    };
  } catch (err: any) {
    if (process.env.NEXT_PUBLIC_ENV === 'production') Sentry.captureException(err);
  }

  return {};
}

class GeolocationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeolocationError';
  }
}

async function getGeoLocationByLatLong(lat: number, long: number): Promise<Partial<IGeoDataByLatLng>> {
  try {
    const geocoder = new google.maps.Geocoder();

    const location: google.maps.GeocoderResult[] = await new Promise((resolve, reject) => {
      return geocoder.geocode({ location: { lat, lng: long } }, (results, status) => {
        if (status === 'OK') {
          resolve(results);
        } else {
          reject(new GeolocationError(`GeolocationByLatLong error: ${status}`));
        }
      });
    });

    const stateRecord = location[0].address_components.find(
      (record) => record.types[0] === 'administrative_area_level_1'
    );
    return { stateRecord, state: stateRecord?.short_name };
  } catch (err: any) {
    if (process.env.NEXT_PUBLIC_ENV === 'production') Sentry.captureException(err);
  }

  return {};
}

export function isUserInsideOfferArea(state: string | undefined, DMA: string[]): boolean {
  if (!state) return false;
  if (!DMA.length) return true;

  for (const dmaState in DMA) {
    if (dmaState.includes(state)) return true;
  }

  return false;
}
